import { useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { useUserStore } from '../../../components/userStore';

const FundsModal = ({ isOpen = false, requestId, onClose }) => {
	const modalRef = useRef();
	const { user } = useUserStore();
	const [selectedFund, setSelectedFund] = useState('');
	const [amount, setAmount] = useState(0);
	useEffect(() => {
		if (isOpen) {
			setSelectedFund('');
			setAmount(0);
			modalRef.current.showModal();
		}
	}, [isOpen]);
	const fundsQuery = useQuery({
		queryKey: ['fundsList', user.coord_agency_id],
		queryFn: async () => {
			const { data } = await axios.get(
				`coord_agencies/${user.coord_agency_id}/funds`,
			);
			return data;
		},
	});
	const linkedFundsQuery = useQuery({
		queryKey: ['linkedFunds', requestId],
		queryFn: async () => {
			const { data } = await axios.get(`requests/${requestId}/funds`, {
				params: {
					filter: JSON.stringify({
						include: ['fund'],
					}),
				},
			});
			return data;
		},
		enabled: isOpen,
	});
	const addFundMutation = useMutation({
		mutationFn: async () => {
			const { data } = await axios.post(`requests/${requestId}/funds`, {
				fund_id: selectedFund,
				amount,
			});
			return data;
		},
		onSuccess: () => {
			linkedFundsQuery.refetch();
			setSelectedFund('');
			setAmount(0);
		},
	});
	const removeFundMutation = useMutation({
		mutationFn: async (linkedFundId) => {
			await axios.delete(`requests/${requestId}/funds/${linkedFundId}`);
		},
		onSuccess: () => {
			linkedFundsQuery.refetch();
		},
	});

	let selectedFundName = '';
	if (selectedFund && fundsQuery?.data) {
		const fund = fundsQuery.data.find(
			(f) => f.id === parseInt(selectedFund, 10),
		);
		if (fund) {
			selectedFundName = fund.name;
		}
	}

	return (
		<dialog id="funds-dialog" className="modal" ref={modalRef}>
			<div className="modal-box">
				<h3>Link Funds to Request</h3>
				<div className="flex-1 form-control">
					<label htmlFor="funds" className="label">
						Add Fund
					</label>
					<select
						id="funds"
						className="select select-bordered select-sm"
						value={selectedFund}
						onChange={({ target }) => setSelectedFund(target.value)}
						autoComplete="off"
						disabled={selectedFund !== ''}
						required
					>
						<option value=""></option>
						{fundsQuery?.data?.map((fund) => (
							<option key={`${fund.id}`} value={fund.id}>
								{fund.name} ({fund.acct_num})
							</option>
						))}
					</select>
				</div>
				<div>
					{linkedFundsQuery?.data?.length === 0 && (
						<p className="py-4">No funds linked to this request</p>
					)}
					{linkedFundsQuery?.data?.length > 0 && (
						<table className="table w-full mt-4 table-sm">
							<thead>
								<tr>
									<th>Fund</th>
									<th>Amount</th>
									<th></th>
								</tr>
							</thead>
							<tbody>
								{linkedFundsQuery?.data?.map((linkedFund) => (
									<tr key={`${linkedFund.id}`}>
										<td>
											{linkedFund.fund.name} ({linkedFund.fund.acct_num})
										</td>
										<td>${linkedFund.amount}</td>
										<td>
											<button
												className="btn btn-sm btn-error"
												onClick={() => removeFundMutation.mutate(linkedFund.id)}
											>
												Remove
											</button>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					)}
					{selectedFund && (
						<div className="flex flex-row gap-4 mt-4">
							<div className="flex-1 form-control">
								<label htmlFor="amount" className="label">
									Fund
								</label>
								<input
									type="text"
									className="input input-ghost input-sm"
									readOnly
									defaultValue={selectedFundName}
								/>
							</div>
							<div className="w-1/4 form-control">
								<label htmlFor="amount" className="label">
									Amount
								</label>
								<input
									type="number"
									id="amount"
									value={amount}
									onChange={({ target }) => setAmount(target.value)}
									className="input input-bordered input-sm"
									min="0"
									step="0.01"
									autoComplete="off"
									required
								/>
							</div>
							<button
								className="self-end btn btn-primary btn-sm"
								onClick={addFundMutation.mutate}
							>
								Add
							</button>
						</div>
					)}
				</div>
				<div className="modal-action">
					<form method="dialog">
						<button className="btn btn-primary" onClick={onClose}>
							Close
						</button>
					</form>
				</div>
			</div>
		</dialog>
	);
};

FundsModal.propTypes = {
	isOpen: PropTypes.bool,
	requestId: PropTypes.string.isRequired,
	onClose: PropTypes.func.isRequired,
};

export default FundsModal;
